import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, TrendingUp, Users, DollarSign, Check, Wallet, ArrowRight, Clock, AlertCircle } from 'lucide-react';
import { userApi, starApi, settingsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export default function UserStars() {
  const [starProgress, setStarProgress] = useState(null);
  const [salaryWallet, setSalaryWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] = useState(false);
  const [blcPrice, setBlcPrice] = useState(0.027);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [starRes, salaryRes, settingsRes] = await Promise.all([
        userApi.getStarProgress(),
        userApi.getSalaryWallet(),
        settingsApi.get()
      ]);
      setStarProgress(starRes.data);
      setSalaryWallet(salaryRes.data);
      setBlcPrice(settingsRes.data.settings?.blc_price || 0.027);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTransferToCash = async () => {
    setTransferring(true);
    try {
      const response = await userApi.transferSalaryToCash();
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  const userStats = starProgress?.user_stats || {};
  const currentStar = starProgress?.current_star;
  const nextStar = starProgress?.next_star;
  const allStars = starProgress?.all_stars || [];
  const usdToBLC = (usd) => blcPrice > 0 ? (Math.abs(usd) / blcPrice).toFixed(2) : '0.00';

  return (
    <div className="space-y-8" data-testid="stars-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-yellow-500/10">
          <Star className="w-6 h-6 text-yellow-400" />
        </div>
        <div>
          <h1 className="text-2xl text-white font-medium">Community Star Program</h1>
          <p className="text-sm text-zinc-500">Achieve star ranks and earn monthly salary</p>
        </div>
      </div>

      {/* Current Status */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Salary Wallet Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-6 border border-emerald-500/30"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-emerald-500/10">
                <Wallet className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase">Salary Wallet</p>
                <p className="text-3xl text-emerald-400 font-bold">${(salaryWallet?.salary_wallet || 0).toFixed(2)}</p>
                <p className="text-xs text-emerald-400/70">{usdToBLC(salaryWallet?.salary_wallet || 0)} BLC</p>
                {salaryWallet?.star_level && (
                  <p className="text-xs text-zinc-400 mt-1">
                    Current Rank: <span className="text-amber-400">{salaryWallet.star_level}</span>
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Button
                onClick={handleTransferToCash}
                disabled={transferring || (salaryWallet?.salary_wallet || 0) <= 0}
                className="gold-btn"
                data-testid="transfer-salary-btn"
              >
                {transferring ? (
                  <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                ) : (
                  <>
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Transfer to Cash
                  </>
                )}
              </Button>
            </div>
          </div>
          
          {/* Next Transfer Date */}
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-zinc-500" />
              <span className="text-zinc-400">Next Transfer Available:</span>
              <span className="text-white">
                {salaryWallet?.next_transfer_date 
                  ? new Date(salaryWallet.next_transfer_date).toLocaleDateString('en-US', { 
                      month: 'long', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })
                  : '1st of next month'}
              </span>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              Salary can only be transferred to Cash Wallet on the 1st of each month
            </p>
          </div>
        </motion.div>

        {/* Current Rank Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6 border-amber-500/20"
        >
          <h2 className="text-white font-medium mb-4">Your Current Rank</h2>
          {currentStar ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-3 pulse-glow">
                <Star className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-xl text-white font-medium">{currentStar.name}</h3>
              <p className="text-amber-400 text-lg mt-1">
                ${currentStar.monthly_salary}/month
                <span className="text-amber-400/70 text-sm ml-1">({usdToBLC(currentStar.monthly_salary)} BLC)</span>
              </p>
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-3">
                <Star className="w-8 h-8 text-zinc-600" />
              </div>
              <h3 className="text-lg text-zinc-400">No Star Rank Yet</h3>
              <p className="text-zinc-500 text-sm mt-1">
                Build your team to achieve your first star!
              </p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Important Notice */}
      <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-400 font-medium text-sm">How Star Salary Works</p>
            <ul className="text-xs text-amber-200 mt-1 space-y-1">
              <li>• Only referrals who <strong>activate staking packages</strong> count towards star requirements</li>
              <li>• Monthly salary is credited to your Salary Wallet on the 1st of each month</li>
              <li>• Salary Wallet funds transfer to Cash Wallet on 1st of every month</li>
              <li>• Cash Wallet funds can be withdrawn on allowed dates or used for staking</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Progress to Next Star */}
      <div className="grid md:grid-cols-2 gap-6">
        {nextStar && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-6"
          >
            <h2 className="text-white font-medium mb-4">Progress to {nextStar.name}</h2>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-400">Direct Referrals</span>
                  <span className="text-white">
                    {userStats.direct_referrals}/{nextStar.direct_referrals}
                  </span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${Math.min(100, (userStats.direct_referrals / nextStar.direct_referrals) * 100)}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-400">Level 2 Referrals</span>
                  <span className="text-white">
                    {userStats.level2_refs}/{nextStar.level2_refs}
                  </span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${Math.min(100, (userStats.level2_refs / nextStar.level2_refs) * 100)}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-400">Business Volume</span>
                  <span className="text-white">
                    ${userStats.business_volume?.toLocaleString() || 0}/${nextStar.business_volume.toLocaleString()}
                  </span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${Math.min(100, (userStats.business_volume / nextStar.business_volume) * 100)}%` }}
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-white/10 text-center">
                <p className="text-zinc-400 text-sm">Monthly Salary at this rank</p>
                <p className="text-2xl text-amber-400 font-medium">${nextStar.monthly_salary} <span className="text-amber-400/70 text-sm">({usdToBLC(nextStar.monthly_salary)} BLC)</span></p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Your Stats (Active Stakers Only) */}
      <div>
        <h3 className="text-white font-medium mb-3 flex items-center gap-2">
          <Users className="w-4 h-4 text-amber-400" />
          Your Team Stats <span className="text-xs text-zinc-500">(Active stakers only)</span>
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Direct', value: userStats.direct_referrals || 0 },
            { label: 'Level 2', value: userStats.level2_refs || 0 },
            { label: 'Level 3', value: userStats.level3_refs || 0 },
            { label: 'Level 4', value: userStats.level4_refs || 0 },
            { label: 'Level 5', value: userStats.level5_refs || 0 },
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="glass-card p-4 text-center"
            >
              <p className="text-xs text-zinc-500 mb-1">{stat.label}</p>
              <p className="text-2xl text-white font-medium">{stat.value}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* All Star Levels */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="glass-card p-6"
      >
        <h2 className="text-white font-medium mb-4">All Star Levels</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {allStars.map((star, idx) => {
            const isAchieved = currentStar?.id === star.id || 
              (currentStar && allStars.findIndex(s => s.id === currentStar.id) > idx);
            
            return (
              <div 
                key={star.id}
                className={`p-4 rounded-xl border ${
                  isAchieved 
                    ? 'border-green-500/30 bg-green-500/5' 
                    : 'border-white/10 bg-black/30'
                }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isAchieved ? 'bg-green-500/20' : 'bg-amber-500/10'
                  }`}>
                    {isAchieved ? (
                      <Check className="w-5 h-5 text-green-400" />
                    ) : (
                      <Star className="w-5 h-5 text-amber-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-white font-medium">{star.name}</p>
                    <p className="text-amber-400 text-sm">${star.monthly_salary}/mo</p>
                  </div>
                </div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Direct</span>
                    <span className="text-zinc-300">{star.direct_referrals}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">L2 Refs</span>
                    <span className="text-zinc-300">{star.level2_refs}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Volume</span>
                    <span className="text-zinc-300">${star.business_volume.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}
